<template>
  <div class="graph-shell" ref="shell">
    <svg viewBox="0 0 700 380" font-family="Inter, system-ui, sans-serif">
      <defs>
        <pattern id="seed-stripes" width="16" height="16" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
          <rect width="16" height="16" fill="#232532"></rect>
          <rect width="7" height="16" fill="#2b2741"></rect>
        </pattern>
      </defs>

      <!-- unpopulated rest web: strands converge on the seed placeholder at center -->
      <Transition name="web-fade">
        <g v-if="state === 'empty'">
          <g stroke="#33363f" fill="none">
            <path d="M 150 130 Q 250 100 350 190"></path>
            <path d="M 550 120 Q 450 105 350 190"></path>
            <path d="M 200 300 Q 275 245 350 190"></path>
            <path d="M 500 310 Q 425 250 350 190"></path>
            <path d="M 150 130 Q 160 230 200 300"></path>
          </g>
          <circle cx="150" cy="130" r="13" fill="#232532" stroke="#4a4d59"></circle>
          <circle cx="550" cy="120" r="13" fill="#232532" stroke="#4a4d59"></circle>
          <circle cx="200" cy="300" r="11" fill="#232532" stroke="#4a4d59"></circle>
          <circle cx="500" cy="310" r="11" fill="#232532" stroke="#4a4d59"></circle>
          <rect x="322" y="148" width="56" height="84" rx="8" fill="url(#seed-stripes)" stroke="#9184d9"></rect>
        </g>
      </Transition>

      <!-- edges (keyed by endpoints so layout changes redraw them) -->
      <line v-for="(edge, index) in neutralEdges" :key="'ne' + index + edgeKey(edge)" class="e-draw" pathLength="1" :style="delayStyle(750 + index * 35)" :x1="edge.x1" :y1="edge.y1" :x2="edge.x2" :y2="edge.y2" stroke="#3f424d" stroke-width="1" />
      <line v-for="(edge, index) in accentEdges" :key="'ae' + index + edgeKey(edge)" class="e-draw" pathLength="1" :style="delayStyle(620 + index * 50)" :x1="edge.x1" :y1="edge.y1" :x2="edge.x2" :y2="edge.y2" stroke="#9184d9" stroke-width="1.5" opacity="0.85" />
      <line v-for="(edge, index) in gapLines" :key="'ge' + index + edgeKey(edge)" :x1="edge.x1" :y1="edge.y1" :x2="edge.x2" :y2="edge.y2" stroke="#3f424d" stroke-width="1" stroke-dasharray="3 6" />
      <text v-if="state === 'nomatch'" x="350" y="196" text-anchor="middle" fill="#595d6c" :font-size="fs(15)">∅</text>
      <ellipse v-if="state === 'hub'" cx="350" cy="190" :rx="HUB_RX + 5" :ry="HUB_RY + 5" fill="none" stroke="#3f424d" stroke-dasharray="2 5" />

      <!-- unshared cast dots -->
      <g v-for="(dot, index) in dots" :key="'d' + dot.key" class="node-move" :transform="`translate(${dot.x} ${dot.y})`">
        <g class="n-pop" :style="delayStyle(700 + index * 45)">
          <circle r="3" fill="#595d6c" />
          <text :x="dot.lx - dot.x" :y="dot.ly - dot.y" :text-anchor="dot.anchor" fill="#9397ab" :font-size="fs(10)">{{ dot.label }}</text>
        </g>
      </g>

      <!-- hub spokes (single selection) -->
      <g v-for="(spoke, index) in spokes" :key="'s' + spoke.id" class="node-move clickable" :transform="`translate(${spoke.x} ${spoke.y})`" @click="$emit('pivot', spoke.raw)">
        <g class="n-pop" :style="delayStyle(550 + index * 60)">
          <template v-if="spoke.shape === 'circle'">
            <circle v-if="spoke.tier === 'hi'" :r="spoke.r + 7" fill="none" stroke="#9184d9" opacity="0.3" />
            <circle :r="spoke.r" :fill="spoke.tier === 'hi' ? '#2b2741' : '#292b31'" :stroke="spoke.stroke" />
            <text :y="fsBound(3.5)" text-anchor="middle" :fill="spoke.tier === 'low' ? '#9397ab' : '#e7e5fe'" :font-size="fsBound(spoke.r > 14 ? 9 : 8)" font-weight="500">{{ spoke.initials }}</text>
            <template v-if="spoke.img">
              <clipPath :id="'clip-spoke-' + spoke.id">
                <circle :r="spoke.r" />
              </clipPath>
              <image :x="-spoke.r" :y="-spoke.r" :width="spoke.r * 2" :height="spoke.r * 2" :href="spoke.img" :clip-path="'url(#clip-spoke-' + spoke.id + ')'" preserveAspectRatio="xMidYMid slice" />
              <circle :r="spoke.r" fill="none" :stroke="spoke.stroke" />
            </template>
          </template>
          <circle v-else r="3" :fill="spoke.tier === 'low' ? '#595d6c' : '#796cbf'" />
          <text :x="spoke.lx - spoke.x" :y="spoke.ly - spoke.y" :text-anchor="spoke.anchor" :fill="spoke.tier === 'low' ? '#9397ab' : '#e9e9ed'" :font-size="fs(spoke.tier === 'hi' ? 11 : 10)">{{ spoke.label }}</text>
          <text v-if="spoke.sub" :x="spoke.lx - spoke.x" :y="spoke.sy - spoke.y" :text-anchor="spoke.anchor" fill="#796cbf" :font-size="fs(9)">{{ spoke.sub }}</text>
        </g>
      </g>

      <!-- selected items -->
      <g v-for="(node, index) in itemNodes" :key="'i' + node.id" class="node-move" :transform="`translate(${node.x} ${node.y})`">
        <!-- radar pings sit outside the entrance group so they neither scale with it nor skew its bbox -->
        <g v-if="node.loading" class="radar">
          <circle
            v-for="ping in 3"
            :key="'ping' + ping"
            :r="pingRadius(node)"
            fill="none"
            stroke="#9184d9"
            vector-effect="non-scaling-stroke"
            class="ping"
            :style="{ animationDelay: `${(ping - 1) * 0.6}s` }"
          />
        </g>
        <!-- class must not depend on chart state: swapping it would replay the entrance on a node that should only glide -->
        <g :class="index === 0 ? 'n-morph' : 'n-pop'" :style="index === 0 ? null : delayStyle(120 + index * 90)">
          <template v-if="node.shape === 'rect'">
            <rect :x="fsBound(-55)" :y="fsBound(-23)" :width="fsBound(110)" :height="fsBound(46)" rx="8" fill="#232532" :stroke="node.stroke" />
            <text :y="fsBound(-2)" text-anchor="middle" fill="#e9e9ed" :font-size="fsBound(11)" font-weight="500">{{ node.label }}</text>
            <text :y="fsBound(13)" text-anchor="middle" fill="#9397ab" :font-size="fsBound(9)">{{ node.sub }}</text>
          </template>
          <template v-else-if="node.shape === 'poster'">
            <clipPath :id="'clip-poster-' + node.id">
              <rect :x="-node.w / 2" :y="-node.h / 2" :width="node.w" :height="node.h" rx="8" />
            </clipPath>
            <!-- pulse lives on an inner group: sharing a node with the entrance would clash on `animation` -->
            <g :class="{ 'is-loading': node.loading }">
              <rect :x="-node.w / 2" :y="-node.h / 2" :width="node.w" :height="node.h" rx="8" fill="url(#seed-stripes)" />
              <image
                v-if="node.img"
                :x="-node.w / 2"
                :y="-node.h / 2"
                :width="node.w"
                :height="node.h"
                :href="node.img"
                :clip-path="'url(#clip-poster-' + node.id + ')'"
                preserveAspectRatio="xMidYMid slice"
              />
              <rect :x="-node.w / 2" :y="-node.h / 2" :width="node.w" :height="node.h" rx="8" fill="none" :stroke="node.stroke" />
            </g>
            <text :y="node.h / 2 + fs(16)" text-anchor="middle" fill="#cfd3e5" :font-size="fs(11)">{{ node.label }}</text>
            <text v-if="node.sub" :y="node.h / 2 + fs(30)" text-anchor="middle" fill="#9397ab" :font-size="fs(10)">{{ node.sub }}</text>
          </template>
          <template v-else>
            <g :class="{ 'is-loading': node.loading }">
              <circle v-if="node.hero" r="33" fill="none" stroke="#9184d9" opacity="0.3" />
              <circle :r="node.r" fill="#2b2741" stroke="#9184d9" />
              <text :y="fsBound(4)" text-anchor="middle" fill="#e7e5fe" :font-size="fsBound(10)" font-weight="500">{{ node.initials }}</text>
              <template v-if="node.img">
                <clipPath :id="'clip-item-' + node.id">
                  <circle :r="node.r" />
                </clipPath>
                <image :x="-node.r" :y="-node.r" :width="node.r * 2" :height="node.r * 2" :href="node.img" :clip-path="'url(#clip-item-' + node.id + ')'" preserveAspectRatio="xMidYMid slice" />
                <circle :r="node.r" fill="none" stroke="#9184d9" />
              </template>
            </g>
            <text :y="(node.hero ? 33 : 28) + fs(11)" text-anchor="middle" fill="#e9e9ed" :font-size="fs(11)">{{ node.label }}</text>
            <text :y="(node.hero ? 33 : 28) + fs(25)" text-anchor="middle" fill="#9397ab" :font-size="fs(9)">{{ node.sub }}</text>
          </template>
        </g>
      </g>

      <!-- shared entities -->
      <g v-for="(shared, index) in sharedNodes" :key="'sh' + shared.id" class="node-move clickable" :transform="`translate(${shared.x} ${shared.y})`" @click="$emit('pivot', shared.raw)">
        <g class="n-pop" :style="delayStyle(580 + index * 80)">
          <template v-if="shared.shape === 'circle'">
            <circle :r="shared.r + 7" fill="none" stroke="#9184d9" opacity="0.25" />
            <circle :r="shared.r" fill="#2b2741" stroke="#9184d9" />
            <text :y="fsBound(3.5)" text-anchor="middle" fill="#e7e5fe" :font-size="fsBound(shared.r > 18 ? 10 : 9)" font-weight="500">{{ shared.initials }}</text>
            <template v-if="shared.img">
              <clipPath :id="'clip-shared-' + shared.id">
                <circle :r="shared.r" />
              </clipPath>
              <image :x="-shared.r" :y="-shared.r" :width="shared.r * 2" :height="shared.r * 2" :href="shared.img" :clip-path="'url(#clip-shared-' + shared.id + ')'" preserveAspectRatio="xMidYMid slice" />
              <circle :r="shared.r" fill="none" stroke="#9184d9" />
            </template>
          </template>
          <template v-else>
            <clipPath :id="'clip-shposter-' + shared.id">
              <rect :x="-shared.w / 2" :y="-shared.h / 2" :width="shared.w" :height="shared.h" rx="7" />
            </clipPath>
            <rect :x="-shared.w / 2" :y="-shared.h / 2" :width="shared.w" :height="shared.h" rx="7" fill="url(#seed-stripes)" />
            <image
              v-if="shared.img"
              :x="-shared.w / 2"
              :y="-shared.h / 2"
              :width="shared.w"
              :height="shared.h"
              :href="shared.img"
              :clip-path="'url(#clip-shposter-' + shared.id + ')'"
              preserveAspectRatio="xMidYMid slice"
            />
            <rect :x="-shared.w / 2" :y="-shared.h / 2" :width="shared.w" :height="shared.h" rx="7" fill="none" stroke="#9184d9" />
          </template>
          <text v-if="shared.labelY !== null" :y="shared.labelY - shared.y" text-anchor="middle" fill="#e9e9ed" :font-size="fs(shared.smallLabel ? 9.5 : 11)">{{ shared.label }}</text>
        </g>
      </g>

      <text v-if="overflowText" x="350" :y="overflowY" text-anchor="middle" fill="#75798c" :font-size="fs(10)">{{ overflowText }}</text>
      <text v-if="hubOverflowText" x="688" y="372" text-anchor="end" fill="#75798c" :font-size="fs(10)">{{ hubOverflowText }}</text>
    </svg>
  </div>
</template>

<script>
  import { rankMixed } from '../helper/tmdb';

  const CX = 350;
  const CY = 190;
  // the svg scales to its container, so a font-size in user units renders at
  // `size * containerWidth / VB_W` physical px — tiny on a phone. fontScale
  // compensates so a body label never renders below TARGET_PX.
  const VB_W = 700;
  const TARGET_PX = 11.5;
  const BODY_SIZE = 10;
  // text boxed inside a node (initials, the title card) can't grow as freely as
  // free-floating labels without bursting its container
  const BOUND_MAX = 1.4;
  // shared results occupy the centre column, so 3+ items sit on the diagonals
  // rather than at top/bottom centre where they would collide with it
  const ANGLE_SETS = { 2: [180, 0], 3: [180, 300, 60], 4: [210, 330, 30, 150] };
  // the hub ring is an ellipse, not a circle: the canvas is far wider than it is
  // tall, so stretching sideways buys ~35% more perimeter — room for more cast —
  // without pushing the top/bottom spokes off the chart
  const HUB_RX = 192;
  const HUB_RY = 130;
  // the credits are the point of the single-selection view, so show more of them
  const HUB_MAX = 12;
  // the ring's top and bottom put ~96px between neighbours, and those labels are
  // centred on the node rather than running outward — so they have to stay
  // narrower than the sideways ones or adjacent titles graze each other
  const HUB_LABEL_CHARS = { middleTight: 16, middle: 20, side: 17 };

  function rad(deg) {
    return (deg * Math.PI) / 180;
  }

  function truncate(text, max) {
    if (!text) return '';
    return text.length > max ? text.slice(0, max - 1) + '…' : text;
  }

  function initials(name) {
    if (!name) return '?';
    return name.split(' ').filter(Boolean).slice(0, 2).map(word => word[0].toUpperCase()).join('');
  }

  function profileImg(path, size = 'w92') {
    return path ? `https://image.tmdb.org/t/p/${size}${path}` : null;
  }

  function posterImg(path) {
    return path ? `https://image.tmdb.org/t/p/w185${path}` : null;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  export default {
    name: 'ConstellationGraph',
    props: {
      items: {
        type: Array,
        required: true
      },
      shared: {
        type: Array,
        required: true
      },
      mode: {
        type: String,
        default: null
      },
      enrich: {
        type: Object,
        default: () => ({})
      }
    },
    emits: ['pivot'],
    data() {
      return { shellWidth: 0, HUB_RX, HUB_RY };
    },
    mounted() {
      this.measure();
      if (typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', this.measure);
        return;
      }
      this.observer = new ResizeObserver(this.measure);
      this.observer.observe(this.$refs.shell);
    },
    beforeUnmount() {
      if (this.observer) this.observer.disconnect();
      else window.removeEventListener('resize', this.measure);
    },
    computed: {
      fontScale() {
        if (!this.shellWidth) return 1;
        const rendered = this.shellWidth / VB_W;
        return clamp(TARGET_PX / (BODY_SIZE * rendered), 1, 2);
      },
      boundScale() {
        return Math.min(this.fontScale, BOUND_MAX);
      },
      state() {
        if (!this.items.length) return 'empty';
        if (this.items.length === 1) return 'hub';
        // don't call it a no-match until every item's credits have actually arrived
        if (this.items.some(item => item.loading)) return 'pending';
        return this.shared.length ? 'match' : 'nomatch';
      },
      itemAngles() {
        const count = this.items.length;
        if (count === 1) return [0];
        if (ANGLE_SETS[count]) return ANGLE_SETS[count];
        return this.items.map((item, index) => 180 + (index * 360) / count);
      },
      itemNodes() {
        const isMovies = this.mode === 'movies';
        if (this.state === 'empty') return [];
        if (this.state === 'hub') {
          const item = this.items[0];
          if (isMovies) {
            return [{
              id: item.uid,
              shape: 'poster',
              x: CX,
              y: CY,
              w: 84,
              h: 126,
              img: posterImg(item.poster),
              stroke: '#9184d9',
              loading: item.loading,
              label: this.trunc(item.label, 20),
              sub: item.loading ? 'Loading cast…' : `${item.year || '—'} · ${item.credits.length} cast`
            }];
          }
          return [{
            id: item.uid,
            shape: 'circle',
            hero: true,
            x: CX,
            y: CY,
            r: 26,
            img: profileImg(item.profile, 'w185'),
            loading: item.loading,
            initials: initials(item.label),
            label: this.trunc(item.label, 20),
            sub: item.loading ? 'Loading credits…' : `${item.credits.length} credits`
          }];
        }
        // posters need more room than the old title cards: pull them in and use
        // compact cards once 3+ items share the canvas with the centre column
        const usePoster = isMovies && this.items.length <= 2;
        return this.items.map((item, index) => {
          const angle = rad(this.itemAngles[index]);
          const x = CX + (usePoster ? 205 : 225) * Math.cos(angle);
          const y = CY + 130 * Math.sin(angle);
          if (usePoster) {
            return {
              id: item.uid,
              shape: 'poster',
              x,
              y,
              w: 84,
              h: 126,
              img: posterImg(item.poster),
              stroke: '#5d5294',
              loading: item.loading,
              label: this.trunc(item.label, 16),
              sub: item.loading ? 'Loading cast…' : String(item.year || '')
            };
          }
          if (isMovies) {
            return {
              id: item.uid,
              shape: 'rect',
              x,
              y,
              stroke: '#595d6c',
              label: this.trunc(item.label, 15, this.boundScale),
              sub: String(item.year || '')
            };
          }
          return {
            id: item.uid,
            shape: 'circle',
            hero: false,
            x,
            y,
            r: 22,
            img: profileImg(item.profile, 'w185'),
            loading: item.loading,
            initials: initials(item.label),
            label: this.trunc(item.label, 18),
            sub: item.loading ? 'Loading credits…' : `${item.credits.length} credits`
          };
        });
      },
      hubCredits() {
        if (this.state !== 'hub') return [];
        const item = this.items[0];
        const credits = this.mode === 'movies'
          ? item.credits
          : rankMixed(item.credits);
        return credits.slice(0, HUB_MAX);
      },
      spokes() {
        if (this.state !== 'hub') return [];
        const isMovies = this.mode === 'movies';
        const count = this.hubCredits.length;
        return this.hubCredits.map((credit, index) => {
          const angle = rad(-90 + (index * 360) / Math.max(count, 1));
          const x = CX + HUB_RX * Math.cos(angle);
          const y = CY + HUB_RY * Math.sin(angle);
          const tier = index === 0 ? 'hi' : index < 3 ? 'mid' : 'low';
          const r = tier === 'hi' ? 18 : tier === 'mid' ? 15 : 13;
          const name = credit.name || credit.title || '';
          const label = this.labelPlacement(x, y, isMovies ? r : 4);
          const extra = this.enrich[credit.uid];
          return {
            id: credit.uid,
            raw: credit,
            shape: isMovies ? 'circle' : 'dot',
            x,
            y,
            r,
            tier,
            stroke: tier === 'hi' ? '#9184d9' : tier === 'mid' ? '#796cbf' : '#595d6c',
            img: profileImg(credit.profile_path),
            initials: initials(name),
            label: this.trunc(name, this.hubLabelChars(label.anchor, count)),
            lx: label.lx,
            ly: label.ly,
            anchor: label.anchor,
            sub: extra ? `+${extra} credits` : '',
            sy: label.ly + (label.above ? -this.fs(12) : this.fs(12))
          };
        });
      },
      sharedShown() {
        if (this.state !== 'match') return [];
        // shared movies are posters, which need more vertical room than actor circles
        return this.shared.slice(0, this.mode === 'movies' ? 4 : 3);
      },
      sharedNodes() {
        const shown = this.sharedShown;
        if (!shown.length) return [];
        const isMovies = this.mode === 'movies';
        const count = shown.length;
        const big = count <= 2;
        const posterSize = big ? { w: 60, h: 90 } : { w: 52, h: 78 };
        const base = isMovies ? (big ? 96 : 62) : (big ? 120 : 106);
        // taller labels need more room between rows, but the column still has to
        // fit the canvas, so cap the total span
        const spacing = count > 1 ? Math.min(base * this.fontScale, 300 / (count - 1)) : base;
        const startY = CY - ((count - 1) * spacing) / 2;
        return shown.map((credit, index) => {
          const y = startY + index * spacing;
          const name = credit.name || credit.title || '';
          if (isMovies) {
            const r = big ? 22 : 16;
            const above = big && index === 0;
            return {
              id: credit.uid,
              raw: credit,
              shape: 'circle',
              x: CX,
              y,
              r,
              img: profileImg(credit.profile_path),
              initials: initials(name),
              label: this.trunc(name, 18),
              labelY: big ? (above ? y - r - this.fs(12) : y + r + this.fs(16)) : y + r + this.fs(12),
              smallLabel: !big
            };
          }
          return {
            id: credit.uid,
            raw: credit,
            shape: 'poster',
            x: CX,
            y,
            w: posterSize.w,
            h: posterSize.h,
            img: posterImg(credit.poster_path),
            label: this.trunc(name, big ? 20 : 18),
            labelY: y + posterSize.h / 2 + this.fs(13),
            smallLabel: !big
          };
        });
      },
      accentEdges() {
        const edges = [];
        if (this.state === 'hub') {
          this.spokes.forEach(spoke => {
            if (spoke.tier !== 'low') edges.push({ x1: CX, y1: CY, x2: spoke.x, y2: spoke.y });
          });
        } else if (this.state === 'match') {
          this.itemNodes.forEach(item => {
            this.sharedNodes.forEach(shared => {
              edges.push({ x1: item.x, y1: item.y, x2: shared.x, y2: shared.y });
            });
          });
        }
        return edges;
      },
      neutralEdges() {
        const edges = [];
        if (this.state === 'hub') {
          this.spokes.forEach(spoke => {
            if (spoke.tier === 'low') edges.push({ x1: CX, y1: CY, x2: spoke.x, y2: spoke.y });
          });
        }
        this.dots.forEach(dot => {
          edges.push({ x1: dot.ix, y1: dot.iy, x2: dot.x, y2: dot.y });
        });
        return edges;
      },
      gapLines() {
        if (this.state !== 'nomatch') return [];
        return this.itemNodes.map(item => ({
          x1: item.x + (CX - item.x) * 0.34,
          y1: item.y + (CY - item.y) * 0.34,
          x2: item.x + (CX - item.x) * 0.86,
          y2: item.y + (CY - item.y) * 0.86
        }));
      },
      dots() {
        if (this.state === 'hub' || this.state === 'empty') return [];
        const sharedIds = new Set(this.shared.map(credit => credit.uid));
        const result = [];
        this.items.forEach((item, index) => {
          const node = this.itemNodes[index];
          const awayAngle = this.itemAngles[index];
          const rest = rankMixed(item.credits.filter(credit => !sharedIds.has(credit.uid))).slice(0, 3);
          rest.forEach((credit, dotIndex) => {
            // wide fan + longer spokes keep these clear of the tall poster beside them
            const angle = rad(awayAngle + (dotIndex - (rest.length - 1) / 2) * 72);
            const x = clamp(node.x + 105 * Math.cos(angle), 40, 660);
            const y = clamp(node.y + 105 * Math.sin(angle), 26, 358);
            const dy = y - node.y;
            // near-horizontal dots sit behind the item node — lift the label above it
            const labelY = dy > 20 ? y + this.fs(15) : Math.abs(dy) < 20 ? node.y - 32 - this.fs(4) : y - this.fs(8);
            result.push({
              // the same credit can hang off two different items (in 2 of 3 picks, say),
              // so the key must be scoped to its owner or the siblings collide
              key: `${index}-${credit.uid}`,
              id: credit.uid,
              x,
              y,
              ix: node.x,
              iy: node.y,
              lx: clamp(x, 62, 638),
              ly: clamp(labelY, 12, 374),
              anchor: 'middle',
              label: this.trunc(credit.name || credit.title || '', 16)
            });
          });
        });
        return result;
      },
      overflowText() {
        const hidden = this.shared.length - this.sharedShown.length;
        if (this.state !== 'match' || hidden <= 0) return '';
        return `+${hidden} more in all`;
      },
      overflowY() {
        const last = this.sharedNodes[this.sharedNodes.length - 1];
        if (!last) return 370;
        return clamp((last.labelY || last.y + 24) + this.fs(18), 40, 374);
      },
      hubOverflowText() {
        if (this.state !== 'hub') return '';
        const hidden = this.items[0].credits.length - this.hubCredits.length;
        if (hidden <= 0) return '';
        return this.mode === 'movies' ? `+${hidden} more cast` : `+${hidden} more credits`;
      }
    },
    methods: {
      measure() {
        const el = this.$refs.shell;
        if (el) this.shellWidth = el.clientWidth;
      },
      fs(size) {
        return Math.round(size * this.fontScale * 100) / 100;
      },
      fsBound(size) {
        return Math.round(size * this.boundScale * 100) / 100;
      },
      // bigger glyphs mean fewer of them fit in the same user-space width
      trunc(text, max, scale = this.fontScale) {
        return truncate(text, Math.max(8, Math.round(max / scale)));
      },
      // a label running outward from the ring's side has the whole margin to itself;
      // one centred above or below shares its row with the neighbours flanking it
      hubLabelChars(anchor, count) {
        if (anchor !== 'middle') return HUB_LABEL_CHARS.side;
        return count > 8 ? HUB_LABEL_CHARS.middleTight : HUB_LABEL_CHARS.middle;
      },
      delayStyle(ms) {
        return { animationDelay: `${ms}ms` };
      },
      pingRadius(node) {
        if (node.shape === 'poster') return node.w / 2 + 4;
        if (node.shape === 'circle') return node.r + 6;
        return 58;
      },
      edgeKey(edge) {
        return `${edge.x1},${edge.y1},${edge.x2},${edge.y2}`;
      },
      labelPlacement(x, y, r) {
        const dx = x - CX;
        const dy = y - CY;
        if (Math.abs(dx) > Math.abs(dy)) {
          return dx > 0
            ? { lx: x + r + 8, ly: y + this.fs(3), anchor: 'start', above: false }
            : { lx: x - r - 8, ly: y + this.fs(3), anchor: 'end', above: false };
        }
        return dy < 0
          ? { lx: x, ly: y - r - this.fs(10), anchor: 'middle', above: true }
          : { lx: x, ly: y + r + this.fs(16), anchor: 'middle', above: false };
      }
    }
  }
</script>

<style scoped>
  .graph-shell {
    flex: 1;
    min-width: 0;
  }
  .web-fade-leave-active {
    transition: opacity 0.22s ease;
  }
  .web-fade-enter-active {
    transition: opacity 0.3s ease 0.25s;
  }
  .web-fade-enter-from,
  .web-fade-leave-to {
    opacity: 0;
  }
  .graph-shell svg {
    width: 100%;
    display: block;
  }
  .clickable {
    cursor: pointer;
  }

  /* existing nodes glide to their new positions on relayout */
  .node-move {
    transition: transform 0.5s cubic-bezier(0.3, 0.7, 0.3, 1);
  }

  /* map being born: nodes spring in, links draw as they land */
  @keyframes node-pop {
    from {
      opacity: 0;
      transform: scale(0.2);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .n-pop {
    animation: node-pop 0.45s cubic-bezier(0.34, 1.45, 0.64, 1) backwards;
    transform-box: fill-box;
    transform-origin: center;
  }

  /* the seed tile becoming your pick: grows from the seed's footprint, never fades */
  @keyframes node-morph {
    from {
      transform: scale(0.67);
    }
    to {
      transform: scale(1);
    }
  }
  .n-morph {
    animation: node-morph 0.42s cubic-bezier(0.34, 1.3, 0.64, 1) backwards;
    transform-box: fill-box;
    transform-origin: center;
  }
  /* node is on the chart but its credits are still in flight: ping outward like radar */
  @keyframes radar-ping {
    from {
      transform: scale(1);
      opacity: 0.5;
    }
    to {
      transform: scale(2.6);
      opacity: 0;
    }
  }
  .ping {
    animation: radar-ping 1.8s ease-out infinite;
    transform-box: fill-box;
    transform-origin: center;
  }
  @keyframes edge-draw {
    from {
      stroke-dashoffset: 1;
    }
    to {
      stroke-dashoffset: 0;
    }
  }
  .e-draw {
    stroke-dasharray: 1;
    animation: edge-draw 0.5s ease-out backwards;
  }
  @media (prefers-reduced-motion: reduce) {
    .node-move {
      transition: none;
    }
    .n-pop,
    .n-morph,
    .e-draw {
      animation: none;
    }
    .radar {
      display: none;
    }
    .is-loading {
      opacity: 0.6;
    }
    .e-draw {
      stroke-dasharray: none;
    }
  }
</style>
